import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { startOfYear, endOfYear } from 'date-fns';
import dateApi from '../services/dateApi';
import { useAuth } from './AuthContext';
import { useResources } from './ResourceContext';

const DateContext = createContext(null);

export const useDates = () => {
  const context = useContext(DateContext);
  if (!context) {
    throw new Error('useDates must be used within a DateProvider');
  }
  return context;
};

export const DateProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { upsertResource } = useResources(); 
  const [dates, setDates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDates = useCallback(async () => {
    if (!isAuthenticated) {
      setDates([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const now = new Date();
      const startDate = startOfYear(now);
      const endDate = endOfYear(now);
      const fetchedDates = await dateApi.getDatesInRange(startDate, endDate);
      setDates(fetchedDates || []);
    } catch (err) {
      console.error("Error loading dates:", err);
      setError(err.message || "Failed to load calendar dates.");
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadDates();
    } else {
      setDates([]);
      setError(null);
    }
  }, [isAuthenticated, loadDates]);

  const addDate = useCallback(async (dateData) => {
    setIsLoading(true);
    try {
      const newDate = await dateApi.createDate(dateData);
      await loadDates(); 
      upsertResource({ ...newDate, type: 'date', resource_type: 'date' }); 
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadDates, upsertResource]);

  const updateDate = useCallback(async (dateId, updatePayload) => {
    setIsLoading(true);
    try {
      const updatedDate = await dateApi.updateDate(dateId, updatePayload);
      await loadDates(); 
      upsertResource({ ...updatedDate, type: 'date', resource_type: 'date' });
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadDates, upsertResource]);

  const deleteDate = useCallback(async (dateId, deletePayload) => {
    setIsLoading(true);
    try {
      await dateApi.deleteDate(dateId, deletePayload);
      await loadDates(); 
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadDates]);

  const contextValue = useMemo(() => ({
    dates,
    isLoading,
    error,
    loadDates,
    addDate,
    updateDate,
    deleteDate,
  }), [dates, isLoading, error, loadDates, addDate, updateDate, deleteDate]);

  return (
    <DateContext.Provider value={contextValue}>
      {children}
    </DateContext.Provider>
  );
};

DateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};